import React, { ButtonHTMLAttributes, forwardRef, FunctionComponent } from "react";
import { useDispatch } from "react-redux";
import { IconButton, ThemeProvider } from "@hxnova/react-components";
import { Icon } from "@hxnova/icons";
import { NovaTheme } from "@hxnova/themes";

import useAvvirSelector from "../../services/component_helpers/component_effects/use_avvir_selector";
import getOrganizationId from "../../services/getters/organization_getters/get_organization_id";
import getOrganizationName from "../../services/getters/organization_getters/get_organization_name";
import getPageName from "../../services/getters/location_metadata/get_page_name";
import getProjectName from "../../services/getters/project_getters/get_project_name";
import { useIsManageUsersAccessible } from "../../queries/user/use_is_manage_users_accessible";
import toOrganizationManageUsers, { ManageUsersBackContext } from "../../events/routing/to_organization_manage_users";
import { TO_SUPERADMIN_ORGANIZATION } from "../../events/routing/superadmin/to_superadmin_organization";
import Tooltipable from "../shared/tooltipable";
import NavBarRedesignIconSlot from "./redesign/nav_bar_redesign_icon_slot";
import { useFlags } from "../../services/utilities/launch_darkly_helper";
import PageNames, { MANAGE_USERS } from "../../models/domain/enums/page_names";

type NovaManageUsersIconButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  selected: boolean;
  redesign: boolean;
};

const NovaManageUsersIconButton = forwardRef<HTMLButtonElement, NovaManageUsersIconButtonProps>(
  ({ selected, redesign, className, ...rest }, ref) => (
    <ThemeProvider theme={NovaTheme} defaultMode="light" storageManager={null}>
      <IconButton
        ref={ref}
        variant="neutral"
        aria-pressed={selected}
        className={`${className ?? ""}${selected ? " Nova-selected" : ""}`}
        {...rest}
      >
        {redesign
          // Reskinned top nav: Nova default weight (300), outline when not selected and filled when
          // selected, per Figma.
          ? <Icon family="material" name="group" filled={selected} size={24}/>
          : <Icon family="material" name="group" filled={selected} size={24} style={{ fontVariationSettings: `"wght" 200, "FILL" ${selected ? 1 : 0}` }}/>}
      </IconButton>
    </ThemeProvider>
  )
);
NovaManageUsersIconButton.displayName = "NovaManageUsersIconButton";

export type Props = {
  // Render the glyph and selection line the reskinned top nav uses (NavBarRedesign). Defaults to the
  // legacy glyph.
  redesign?: boolean;
};

const NavBarManageUsersIcon: FunctionComponent<Props> = ({ redesign = false }) => {
  const dispatch = useDispatch();
  const organizationId = useAvvirSelector(getOrganizationId);
  const accessible = useIsManageUsersAccessible();
  const { customerFacingManageUsers } = useFlags();
  const pageName = useAvvirSelector(getPageName);
  const isActive = pageName === MANAGE_USERS;
  const locationMetadata = useAvvirSelector(state => state.locationMetadata);
  const location = useAvvirSelector(state => state.location);
  const projectName = useAvvirSelector(getProjectName);
  const organizationName = useAvvirSelector(getOrganizationName);

  if (!customerFacingManageUsers || !accessible || !organizationId) return null;

  const handleClick = () => {
    let backContext: ManageUsersBackContext | undefined;

    if (locationMetadata.isSuperadmin && locationMetadata.organizationId && pageName === PageNames.ORGANIZATION) {
      backContext = {
        label: organizationName || organizationId,
        backRouteType: TO_SUPERADMIN_ORGANIZATION,
        backRoutePayload: { organizationId: locationMetadata.organizationId },
      };
    } else if (locationMetadata.projectId) {
      backContext = {
        label: projectName || "Project",
        backRouteType: location.type,
        backRoutePayload: location.payload as Record<string, any>,
      };
    }

    dispatch(toOrganizationManageUsers(organizationId, backContext));
  };

  const icon = (
    <Tooltipable title="Manage users">
      <NovaManageUsersIconButton
        selected={isActive}
        redesign={redesign}
        className="NavBar-manageUsersIcon"
        aria-label="Manage users"
        data-testid="nav-bar-manage-users-icon"
        onClick={handleClick}
      />
    </Tooltipable>
  );

  if (!redesign) return icon;

  // Reskinned top nav: like the row's other icon buttons, the icon sits in a slot that draws the
  // selection line while it is selected.
  return <NavBarRedesignIconSlot selected={isActive}>{icon}</NavBarRedesignIconSlot>;
};

export default NavBarManageUsersIcon;
